/**
 * Product-led onboarding enums, shared across `apps/api` and `apps/tenant`.
 *
 * Church registration happens AFTER authentication, so there is no draft to carry a configuration across the
 * auth boundary and no draft lifecycle to model here (ADR-0020's pre-auth wizard is superseded).
 */

/** The UI languages the onboarding wizard supports — mirrors the tenant app locales. */
export enum OnboardingLanguage {
  French = "fr",
  English = "en",
  Kinyarwanda = "rw",
}

/**
 * What the signed-in user's relationship is to a church already registered under the name they typed.
 *
 * The name check is a courtesy rather than a gate, but the way out it offers has to be real. Resolved on the
 * server: whether someone belongs to a church is an access question, and the client is never the one to answer
 * it.
 */
export enum ChurchNameConflict {
  /** Nobody has this name. */
  None = "NONE",
  /** Taken, and the caller has neither membership nor a live invitation — a genuine namesake. */
  Unrelated = "UNRELATED",
  /** Taken by a church the caller is already an active member of. */
  Member = "MEMBER",
  /** Taken by a church holding a live invitation for the caller's own address. */
  Invited = "INVITED",
}

/**
 * The onboarding creator's self-declared role/position in the church (NOT an authorization role — the creator
 * always becomes the workspace OWNER). Captured for onboarding personalization + who-are-our-users insight.
 *
 * Named by FUNCTION, not by tradition. The first version read Pastor / Elder / Deacon, which is Protestant
 * vocabulary: a Catholic curé, an Orthodox priest or an économe opened this list and found nobody resembling
 * themselves, and "Autre" is a poor greeting for a whole confession. So each row names several traditions'
 * words for the same job.
 *
 * Deliberately SHORT. These are the four people who actually set a church up on software; every other role
 * exists in churches but rarely does this, and `Other` covers them better than four more rows nobody reads.
 */
export enum OnboardingRole {
  /** Leads one congregation: pastor, priest, curé, vicaire, minister, reverend. */
  Clergy = "CLERGY",
  /**
   * Leads several: bishop, archbishop, apostle, overseer, superintendent, national president.
   *
   * Its own row rather than a sixth word in `Clergy` — an apostle or a bishop is not a pastor with a longer
   * title, they oversee other churches, and lumping them together is exactly the flattening that made this
   * list feel like it was written for one kind of church.
   */
  Overseer = "OVERSEER",
  /** Runs the place: parish administrator, church manager, secretary, registrar. */
  Administrator = "ADMINISTRATOR",
  /** Holds the money: treasurer, bursar, économe, accountant. */
  Treasurer = "TREASURER",
  /** Elder, deacon, parish council, ministry leader, anyone else — this row is why the list stays short. */
  Other = "OTHER",
}
